import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import SubscriptionTierIndicator from '../../components/ui/SubscriptionTierIndicator';
import PortfolioSummaryCards from './components/PortfolioSummaryCards';
import PortfolioTable from './components/PortfolioTable';
import PortfolioCharts from './components/PortfolioCharts';
import BatchProcessingPanel from './components/BatchProcessingPanel';
import AlertsPanel from './components/AlertsPanel';
import RecentActivityPanel from './components/RecentActivityPanel';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const PortfolioDashboard = () => {
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filterConfig, setFilterConfig] = useState({
    assetType: 'all',
    ltvRange: 'all',
    riskLevel: 'all',
    valuationAge: 'all'
  });

  // Mock portfolio data
  const portfolioMetrics = {
    totalValue: "$47.2M",
    valueChange: 2.4,
    averageLTV: 68,
    ltvChange: -1.2,
    highRiskCount: 12,
    riskChange: -2,
    pendingValuations: 8
  };

  const portfolioAssets = [
    {
      id: 1,
      assetName: "Liebherr LTM 1200-5.1",
      assetType: "Mobile Crane",
      bookValue: 850000,
      marketValue: 920000,
      ltv: 72,
      valueChange: 3.2,
      lastValuation: "2025-11-01T10:30:00Z",
      riskLevel: "medium"
    },
    {
      id: 2,
      assetName: "Tadano ATF 130G-5",
      assetType: "All Terrain Crane",
      bookValue: 1200000,
      marketValue: 1050000,
      ltv: 89,
      valueChange: -8.1,
      lastValuation: "2025-10-28T14:15:00Z",
      riskLevel: "high"
    },
    {
      id: 3,
      assetName: "Grove GMK4100L-1",
      assetType: "Mobile Crane",
      bookValue: 750000,
      marketValue: 825000,
      ltv: 65,
      valueChange: 5.7,
      lastValuation: "2025-11-03T09:45:00Z",
      riskLevel: "low"
    },
    {
      id: 4,
      assetName: "Manitowoc MLC300",
      assetType: "Crawler Crane",
      bookValue: 2100000,
      marketValue: 1950000,
      ltv: 78,
      valueChange: -2.3,
      lastValuation: "2025-10-30T16:20:00Z",
      riskLevel: "medium"
    },
    {
      id: 5,
      assetName: "Link-Belt RTC-8090 II",
      assetType: "Rough Terrain Crane",
      bookValue: 650000,
      marketValue: 680000,
      ltv: 58,
      valueChange: 1.8,
      lastValuation: "2025-11-02T11:10:00Z",
      riskLevel: "low"
    }
  ];

  const chartData = {
    performance: [
      { month: 'Jul', portfolioValue: 45200000, marketValue: 44800000 },
      { month: 'Aug', portfolioValue: 46100000, marketValue: 45600000 },
      { month: 'Sep', portfolioValue: 46800000, marketValue: 46200000 },
      { month: 'Oct', portfolioValue: 47000000, marketValue: 46800000 },
      { month: 'Nov', portfolioValue: 47200000, marketValue: 47100000 }
    ],
    ltvDistribution: [
      { name: 'Low Risk (0-60%)', value: 8 },
      { name: 'Medium Risk (61-80%)', value: 15 },
      { name: 'High Risk (81-100%)', value: 7 }
    ],
    riskAnalysis: [
      { category: 'Mobile Cranes', count: 12, value: 18.5 },
      { category: 'All Terrain', count: 8, value: 15.2 },
      { category: 'Crawler Cranes', count: 6, value: 9.8 },
      { category: 'Rough Terrain', count: 4, value: 3.7 }
    ],
    insights: {
      totalAssets: 30,
      avgLTV: 68,
      totalValue: 47200000
    }
  };

  const batchJobs = [
    {
      id: 1,
      name: "Monthly Portfolio Revaluation",
      status: "processing",
      processedCount: 18,
      totalCount: 30,
      startedAt: "2025-11-06T14:30:00Z"
    },
    {
      id: 2,
      name: "Q4 Risk Assessment",
      status: "completed",
      processedCount: 25,
      totalCount: 25,
      startedAt: "2025-11-05T09:15:00Z",
      completedAt: "2025-11-05T11:45:00Z"
    },
    {
      id: 3,
      name: "New Asset Integration",
      status: "queued",
      processedCount: 0,
      totalCount: 5,
      startedAt: null
    }
  ];

  const portfolioAlerts = [
    {
      id: 1,
      type: "ltv_breach",
      severity: "high",
      status: "active",
      title: "LTV Threshold Exceeded",
      message: "Tadano ATF 130G-5 has exceeded 85% LTV threshold",
      timestamp: "2025-11-06T13:45:00Z",
      assetDetails: {
        name: "Tadano ATF 130G-5",
        ltv: 89
      }
    },
    {
      id: 2,
      type: "value_drop",
      severity: "medium",
      status: "active",
      title: "Significant Value Decline",
      message: "Manitowoc MLC300 market value dropped by 8.1% this month",
      timestamp: "2025-11-06T10:20:00Z",
      assetDetails: {
        name: "Manitowoc MLC300",
        ltv: 78
      }
    },
    {
      id: 3,
      type: "stale_valuation",
      severity: "low",
      status: "active",
      title: "Valuation Update Required",
      message: "3 assets require valuation updates (>30 days old)",
      timestamp: "2025-11-06T08:15:00Z"
    }
  ];

  const recentActivities = [
    {
      id: 1,
      type: "valuation",
      title: "Asset Valuation Completed",
      description: "Grove GMK4100L-1 valuation updated with current market data",
      timestamp: "2025-11-06T15:30:00Z",
      user: "Sarah Johnson",
      metadata: {
        asset_name: "Grove GMK4100L-1",
        new_value: "$825,000",
        ltv_change: "-3%"
      }
    },
    {
      id: 2,
      type: "alert",
      title: "LTV Alert Triggered",
      description: "High-risk asset threshold breach detected",
      timestamp: "2025-11-06T13:45:00Z",
      user: "System",
      metadata: {
        asset_count: "1",
        severity: "High"
      }
    },
    {
      id: 3,
      type: "batch_job",
      title: "Batch Revaluation Started",
      description: "Monthly portfolio revaluation job initiated",
      timestamp: "2025-11-06T14:30:00Z",
      user: "Michael Chen",
      status: "processing",
      metadata: {
        total_assets: "30",
        estimated_time: "45 minutes"
      }
    },
    {
      id: 4,
      type: "report",
      title: "Portfolio Report Generated",
      description: "Q4 risk assessment report completed and exported",
      timestamp: "2025-11-06T11:20:00Z",
      user: "David Wilson",
      metadata: {
        report_type: "Risk Assessment",
        file_size: "2.4 MB"
      }
    }
  ];

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/crane-search-dashboard' },
    { label: 'Portfolio Management', path: null }
  ];

  const handleAssetSelect = (asset) => {
    setSelectedAsset(asset);
    // Navigate to asset detail view or open modal
    console.log('Selected asset:', asset);
  };

  const handleBatchRevalue = (assetIds) => {
    console.log('Starting batch revaluation for assets:', assetIds);
    // Implement batch revaluation logic
  };

  const handleStartBatch = () => {
    console.log('Starting new batch job');
    // Implement batch job creation
  };

  const handleCancelJob = (jobId) => {
    console.log('Cancelling job:', jobId);
    // Implement job cancellation
  };

  const handleConfigureAlert = () => {
    console.log('Opening alert configuration');
    // Navigate to alert settings
  };

  const handleDismissAlert = (alertId) => {
    console.log('Dismissing alert:', alertId);
    // Implement alert dismissal
  };

  const handleExportReport = () => {
    console.log('Exporting portfolio report');
    // Implement report export
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const applyFilters = (newFilters) => {
    setFilterConfig(newFilters);
    // Implement filtering logic
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <QuickActionToolbar />
      <main className="px-6 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-semibold text-text-primary mb-2">
                Portfolio Dashboard
              </h1>
              <Breadcrumbs items={breadcrumbItems} />
            </div>
            <div className="flex items-center space-x-4">
              <SubscriptionTierIndicator tier="pro" />
              <Button
                variant="outline"
                size="sm"
                onClick={toggleFilters}
                iconName="Filter"
                iconPosition="left"
              >
                Filters
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleExportReport}
                iconName="Download"
                iconPosition="left"
              >
                Export Report
              </Button>
            </div>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="bg-card border border-border rounded-lg p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Asset Type
                  </label>
                  <select 
                    className="w-full px-3 py-2 border border-border rounded-md bg-input text-text-primary"
                    value={filterConfig?.assetType}
                    onChange={(e) => applyFilters({...filterConfig, assetType: e?.target?.value})}
                  >
                    <option value="all">All Types</option>
                    <option value="mobile">Mobile Cranes</option>
                    <option value="crawler">Crawler Cranes</option>
                    <option value="tower">Tower Cranes</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    LTV Range
                  </label>
                  <select 
                    className="w-full px-3 py-2 border border-border rounded-md bg-input text-text-primary"
                    value={filterConfig?.ltvRange}
                    onChange={(e) => applyFilters({...filterConfig, ltvRange: e?.target?.value})}
                  >
                    <option value="all">All Ranges</option>
                    <option value="low">0-60%</option>
                    <option value="medium">61-80%</option>
                    <option value="high">81-100%</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Risk Level
                  </label>
                  <select 
                    className="w-full px-3 py-2 border border-border rounded-md bg-input text-text-primary"
                    value={filterConfig?.riskLevel}
                    onChange={(e) => applyFilters({...filterConfig, riskLevel: e?.target?.value})}
                  >
                    <option value="all">All Levels</option>
                    <option value="low">Low Risk</option>
                    <option value="medium">Medium Risk</option>
                    <option value="high">High Risk</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Valuation Age
                  </label>
                  <select 
                    className="w-full px-3 py-2 border border-border rounded-md bg-input text-text-primary"
                    value={filterConfig?.valuationAge}
                    onChange={(e) => applyFilters({...filterConfig, valuationAge: e?.target?.value})}
                  >
                    <option value="all">All Ages</option>
                    <option value="recent">Last 30 days</option>
                    <option value="moderate">30-90 days</option>
                    <option value="stale">&gt;90 days</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Summary Cards */}
        <PortfolioSummaryCards portfolioMetrics={portfolioMetrics} />

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 mb-8">
          {/* Portfolio Table - Takes 2 columns */}
          <div className="xl:col-span-2">
            <PortfolioTable
              assets={portfolioAssets}
              onAssetSelect={handleAssetSelect}
              onBatchRevalue={handleBatchRevalue}
            />
          </div>

          {/* Side Panel - Takes 1 column */}
          <div className="space-y-6">
            <BatchProcessingPanel
              jobs={batchJobs}
              onStartBatch={handleStartBatch}
              onCancelJob={handleCancelJob}
            />
            
            <AlertsPanel
              alerts={portfolioAlerts}
              onConfigureAlert={handleConfigureAlert}
              onDismissAlert={handleDismissAlert}
            />
          </div>
        </div>

        {/* Charts Section */}
        <div className="mb-8">
          <PortfolioCharts chartData={chartData} />
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <RecentActivityPanel activities={recentActivities} />
          
          {/* Additional Metrics Panel */}
          <div className="bg-card border border-border rounded-lg shadow-card p-6">
            <h2 className="text-lg font-semibold text-text-primary mb-6">Portfolio Insights</h2>
            
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex items-center space-x-3">
                  <Icon name="TrendingUp" size={20} className="text-success" />
                  <div>
                    <div className="font-medium text-text-primary">Portfolio Growth</div>
                    <div className="text-sm text-text-secondary">Month over month</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-semibold text-success">+2.4%</div>
                  <div className="text-xs text-text-secondary">$1.1M increase</div>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex items-center space-x-3">
                  <Icon name="Shield" size={20} className="text-warning" />
                  <div>
                    <div className="font-medium text-text-primary">Risk Exposure</div>
                    <div className="text-sm text-text-secondary">High-risk assets</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-semibold text-warning">23%</div>
                  <div className="text-xs text-text-secondary">7 of 30 assets</div>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex items-center space-x-3">
                  <Icon name="Clock" size={20} className="text-accent" />
                  <div>
                    <div className="font-medium text-text-primary">Avg. Valuation Age</div>
                    <div className="text-sm text-text-secondary">Portfolio average</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-semibold text-text-primary">42 days</div>
                  <div className="text-xs text-text-secondary">Within target</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PortfolioDashboard;